import React, { useContext, useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Divider,
  Switch,
  Grid,
  GridItem,
  useDisclosure,
  useToast,
  Text,
  Progress,
} from "@chakra-ui/react";
import { useDropzone } from "react-dropzone";
import { AuthContext } from "context/AuthContext";
import { H2 } from "pages/Pass-Form/stylePases";
import { IconGeneric } from "styled/Icons.styled";
import { BiImageAdd } from "react-icons/bi";
import { MdCameraAlt } from "react-icons/md";
import Swal from "sweetalert2";
import PassCrudContext from "context/PassCrudContext";
import SpinnerComponent from "components/Spinner/SpinnerComponent";
import isArrayNotEmpty from "utils/type-check-utils";
import { MdClear, MdOutlineImage } from "react-icons/md";
import { ButtonStyled } from "styled/Buttons.styled";
import ReactCardWithIcon from "components/DispatchCorrections/ReactCardWithIcon";
import ZoomImage from "components/DispatchCorrections/ZoomedImage";
import { Modalcustomizable } from "components/modal/ModalPass";

const MAX_FILE_SIZE_BYTES = 3145728; // 3MB
const MAX_NUMBER_OF_FILES = 30;
const ERROR_MESSAGE_SIZE = "excede el límite permitido de 3 Megabytes";
const ERROR_MESSAGE_QUANTITY = `Supera el límite de fotografías permitidas (${MAX_NUMBER_OF_FILES})`;

export const AddPhotography = ({
  photography = [],
  setPhotography = () => {},
  isLoading = false,
  updateAndCreate = () => {},
  title = "Evidencia Fotográfica",
  disableUpload = false,
  activeDelete = true,
  // EF4-1575: si se pasa `canManage`, controla quién puede subir/borrar (para fotos de seguridad).
  // Si es null, se mantiene el comportamiento actual (admin || comercio exterior).
  canManage = null,
  // EF4-1575: si se pasa, se usa en lugar de PassCrudContext.deletePhoto (borrado de foto de seguridad).
  deleteHandler = null,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenCameraPreview,
    onOpen: onOpenCameraPreview,
    onClose: onCloseCameraPreview,
  } = useDisclosure();
  const { authUser } = useContext(AuthContext);
  const [stateImg, setStateImg] = useState(false);
  // EF4-1575: en el módulo de seguridad no existe PassCrudProvider; evitar romper el destructuring.
  const { deletePhoto } = useContext(PassCrudContext) || {};
  const [previewImage, setPreviewImage] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStartTime, setUploadStartTime] = useState(null);
  const toast = useToast();

  // EF4-1575: id único por instancia para evitar colisión de ids del DOM cuando
  // hay varias secciones <AddPhotography/> en la misma página (comercio exterior
  // y fotos de seguridad). Sin esto, getElementById agarra siempre el primer input.
  const instanceUid = useMemo(
    () => `ap-${Math.random().toString(36).slice(2, 10)}`,
    []
  );
  const displayInputId = `display-img-${instanceUid}`;
  const cameraInputId = `camera-input-${instanceUid}`;

  const imgAd = useCallback(() => setStateImg((prev) => !prev), []);

  const UploadImg = useCallback(() => {
    document.getElementById(displayInputId).click();
  }, [displayInputId]);

  const TakePhoto = useCallback(() => {
    document.getElementById(cameraInputId).click();
  }, [cameraInputId]);

  const validateFileSize = useCallback(
    (file) => file.size <= MAX_FILE_SIZE_BYTES,
    []
  );

  const validateFileCount = useCallback(
    (files) => files?.length + photography?.length <= MAX_NUMBER_OF_FILES,
    [photography]
  );

  const deleteImaServer = useCallback(
    (index) => {
      Swal.fire({
        title: `¿Estás seguro de eliminar la fotografía "${index.name_photography}"?`,
        icon: "warning",
        showCancelButton: true,
        cancelButtonColor: "#d33",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#1F7AC3",
        confirmButtonText: "Eliminar",
        reverseButtons: true,
        customClass: { title: "class-text" },
      }).then(async (result) => {
        if (result.isConfirmed) {
          const formData = new FormData();
          formData.append("photography", index.name_photography);
          try {
            // EF4-1575: borrado propio para fotos de seguridad, o el del pase por defecto.
            if (deleteHandler) {
              await deleteHandler(index);
            } else {
              await deletePhoto(formData, index.id);
            }
            const newPhotography = photography.filter(
              (e) => e.name_photography !== index.name_photography
            );
            setPhotography(newPhotography);
            /*  toast({
              title: "Fotografía eliminada con éxito",
              status: "success",
              duration: 3000,
              isClosable: true,
            }); */
          } catch (error) {
            console.error("Error al eliminar la fotografía:", error);
            if (error.response && error.response.data.status.code === 403) {
              toast({
                title:
                  error.response.data.status.message ||
                  "Error al eliminar la fotografía",
                status: "error",
                duration: 3000,
                isClosable: true,
              });
            } else {
              toast({
                title: "Error al eliminar la fotografía",
                description:
                  "Ocurrió un error inesperado. Por favor, inténtelo de nuevo.",
                status: "error",
                duration: 3000,
                isClosable: true,
              });
            }
          }
        }
      });
    },
    [photography, setPhotography, deletePhoto, deleteHandler, toast]
  );

  const handleImageSelection = useCallback(
    (event) => {
      const files = Array.from(event.target.files);

      if (!files?.length) return;

      if (!validateFileCount(files)) {
        toast({
          title: ERROR_MESSAGE_QUANTITY,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const validFiles = files.filter((file) => validateFileSize(file));
      const invalidFiles = files.filter((file) => !validateFileSize(file));

      invalidFiles.forEach((file) => {
        toast({
          title: `La fotografía ${file.name} ${ERROR_MESSAGE_SIZE}`,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      });

      if (validFiles?.length) {
        setUploadProgress(0);
        setIsUploading(true);
        setUploadStartTime(Date.now());
        validFiles.forEach((file, index) => {
          // Intentar llamar con callback de progreso, si no existe, usar sin callback
          const progressCallback = (progress) => {
            setUploadProgress((prevProgress) => {
              const newProgress = prevProgress + progress / validFiles?.length;
              return Math.min(newProgress, 100);
            });
          };
          try {
            updateAndCreate(event, file, progressCallback);
          } catch (error) {
            // Si falla, intentar sin callback
            updateAndCreate(event, file);
            // Si no hay callback, simular progreso completo después de un tiempo
            setTimeout(() => {
              setUploadProgress(100);
            }, 2000);
          }
        });
      }

      event.target.value = null;
    },
    [validateFileCount, validateFileSize, updateAndCreate, toast]
  );

  const handleDrop = useCallback(
    (acceptedFiles) => {
      if (!acceptedFiles?.length) return;

      if (!validateFileCount(acceptedFiles)) {
        toast({
          title: ERROR_MESSAGE_QUANTITY,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const validFiles = acceptedFiles.filter((file) => validateFileSize(file));
      const invalidFiles = acceptedFiles.filter(
        (file) => !validateFileSize(file)
      );

      invalidFiles.forEach((file) => {
        toast({
          title: `La fotografía ${file.name} ${ERROR_MESSAGE_SIZE}`,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      });

      if (validFiles?.length) {
        setUploadProgress(0);
        setIsUploading(true);
        setUploadStartTime(Date.now());
        validFiles.forEach((file) => {
          const syntheticEvent = {
            preventDefault: () => {},
            target: {
              files: [file],
              id: "display-img",
            },
          };
          const progressCallback = (progress) => {
            setUploadProgress((prevProgress) => {
              const newProgress = prevProgress + progress / validFiles?.length;
              return Math.min(newProgress, 100);
            });
          };
          try {
            updateAndCreate(syntheticEvent, file, progressCallback);
          } catch (error) {
            // Si falla, intentar sin callback
            updateAndCreate(syntheticEvent, file);
            // Si no hay callback, simular progreso completo después de un tiempo
            setTimeout(() => {
              setUploadProgress(100);
            }, 2000);
          }
        });
      }
    },
    [validateFileCount, validateFileSize, updateAndCreate, toast]
  );

  const handleCameraCapture = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!validateFileSize(file)) {
        toast({
          title: `La fotografía ${file.name} ${ERROR_MESSAGE_SIZE}`,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        event.target.value = null;
        return;
      }

      if (!validateFileCount([file])) {
        toast({
          title: ERROR_MESSAGE_QUANTITY,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        event.target.value = null;
        return;
      }

      // Crear una URL temporal para previsualizar la foto
      const reader = new FileReader();
      reader.onloadend = () => {
        setCapturedPhoto({
          file: file,
          preview: reader.result,
        });
        onOpenCameraPreview();
      };
      reader.readAsDataURL(file);
    },
    [validateFileSize, validateFileCount, toast, onOpenCameraPreview]
  );

  const confirmCapturedPhoto = useCallback(() => {
    if (!capturedPhoto) return;

    // Crear un evento sintético similar al que genera el input file
    const syntheticEvent = {
      preventDefault: () => {},
      target: {
        files: [capturedPhoto.file],
        id: "display-img", // Usar el mismo id que el input de imágenes
      },
    };

    setUploadProgress(0);
    setIsUploading(true);
    setUploadStartTime(Date.now());
    // Llamar a updateAndCreate de la misma manera que handleImageSelection
    const progressCallback = (progress) => {
      setUploadProgress((prevProgress) => {
        const newProgress = prevProgress + progress;
        return Math.min(newProgress, 100);
      });
    };
    try {
      updateAndCreate(syntheticEvent, capturedPhoto.file, progressCallback);
    } catch (error) {
      // Si falla, intentar sin callback
      updateAndCreate(syntheticEvent, capturedPhoto.file);
      // Si no hay callback, simular progreso completo después de un tiempo
      setTimeout(() => {
        setUploadProgress(100);
      }, 2000);
    }

    // Limpiar estado
    setCapturedPhoto(null);
    onCloseCameraPreview();
    document.getElementById(cameraInputId).value = null;
  }, [capturedPhoto, updateAndCreate, onCloseCameraPreview, cameraInputId]);

  const discardCapturedPhoto = useCallback(() => {
    setCapturedPhoto(null);
    onCloseCameraPreview();
    document.getElementById(cameraInputId).value = null;
  }, [onCloseCameraPreview, cameraInputId]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  }, []);

  const openImageView = useCallback(
    (url) => {
      setPreviewImage(url);
      setIsFullscreen(false);
      onOpen();
    },
    [onOpen]
  );

  const remainingPhotos = useMemo(
    () => MAX_NUMBER_OF_FILES - photography?.length,
    [photography]
  );

  // EF4-1575: gate de gestión (subir/borrar). Por defecto admin || comercio exterior.
  const canManagePhotos = useMemo(() => {
    return canManage !== null
      ? canManage
      : authUser?.admin || authUser?.is_commerce;
  }, [canManage, authUser?.admin, authUser?.is_commerce]);

  const shouldShowSwitch = useMemo(() => {
    return canManagePhotos && !disableUpload;
  }, [canManagePhotos, disableUpload]);

  // Efecto para deshabilitar el loading cuando la carga se complete
  useEffect(() => {
    if (uploadProgress >= 100 && isUploading) {
      const timer = setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
        setUploadStartTime(null);
      }, 500); // Esperar medio segundo después de completar
      return () => clearTimeout(timer);
    }
  }, [uploadProgress, isUploading]);

  // Timeout de seguridad: desbloquear después de 15 segundos si aún está cargando
  useEffect(() => {
    if (isUploading && uploadStartTime) {
      const elapsed = Date.now() - uploadStartTime;
      const safetyTimeout = setTimeout(() => {
        if (isUploading) {
          setIsUploading(false);
          setUploadProgress(0);
          setUploadStartTime(null);
        }
      }, Math.max(0, 15000 - elapsed)); // 15 segundos desde el inicio

      return () => clearTimeout(safetyTimeout);
    }
  }, [isUploading, uploadStartTime]);

  // Detectar cuando se agregan nuevas fotografías para desbloquear
  const prevPhotographyLength = useRef(photography?.length || 0);
  useEffect(() => {
    if (isUploading && photography?.length > prevPhotographyLength.current) {
      // Se agregó una nueva fotografía, esperar un momento y desbloquear
      const timer = setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
        setUploadStartTime(null);
      }, 1000);
      prevPhotographyLength.current = photography?.length;
      return () => clearTimeout(timer);
    } else if (photography?.length !== prevPhotographyLength.current) {
      prevPhotographyLength.current = photography?.length;
    }
  }, [photography, isUploading]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp"],
    },
    multiple: true,
    disabled: !stateImg || !shouldShowSwitch || remainingPhotos === 0 || isUploading,
  });

  if (isLoading) return <SpinnerComponent />;

  return (
    <>
      <Modalcustomizable
        title="Previsualización"
        isOpen={isOpen}
        onClose={onClose}
        rightButtonText="Confirmar"
        iconComponent={MdOutlineImage}
        hiddenButtons={true}
        size={isFullscreen ? "full" : "6xl"}
      >
        <ZoomImage
          src={previewImage}
          toggleFullscreen={toggleFullscreen}
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
        />
      </Modalcustomizable>

      <Modalcustomizable
        title="Foto capturada"
        isOpen={isOpenCameraPreview}
        onClose={discardCapturedPhoto}
        rightButtonText="Subir foto"
        iconComponent={MdCameraAlt}
        hiddenButtons={false}
        size="4xl"
        rightButtonColor="blue"
        onRightButtonClick={confirmCapturedPhoto}
      >
        <Box>
          {capturedPhoto && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                as="img"
                src={capturedPhoto.preview}
                alt="Foto capturada"
                maxH="500px"
                maxW="100%"
                objectFit="contain"
                mb={4}
                borderRadius="md"
                boxShadow="md"
              />
              <Text fontSize="sm" color="gray.600" textAlign="center" mb={2}>
                Revisa la foto capturada. Puedes descartarla o subirla al sistema.
              </Text>
            </Box>
          )}
        </Box>
      </Modalcustomizable>

      <Box mt={3}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <H2>{title}</H2>
            {shouldShowSwitch ? (
              <Switch
                name="generic_subheading_declaration"
                id="generic_subheading_declaration"
                size="md"
                isChecked={stateImg}
                onChange={imgAd}
                ml={3}
              />
            ) : null}
          </Box>
          {!disableUpload && (
            <Text>Fotografías restantes: {remainingPhotos}</Text>
          )}
        </Box>
        {stateImg && shouldShowSwitch ? (
          <>
            <Box mt={1} display="flex" gap={2} flexWrap="wrap" mb={3}>
              <ButtonStyled
                className="d-flex justify-content-center align-items-center"
                bgColor="orange"
                onClick={UploadImg}
                isDisabled={remainingPhotos === 0 || isUploading}
                isLoading={isUploading}
                loadingText="Subiendo..."
              >
                Adjuntar imágenes
                <IconGeneric className="text-white ms-1" as={BiImageAdd} />
              </ButtonStyled>
              <ButtonStyled
                className="d-flex justify-content-center align-items-center"
                bgColor="blue"
                onClick={TakePhoto}
                isDisabled={remainingPhotos === 0 || isUploading}
                isLoading={isUploading}
                loadingText="Subiendo..."
              >
                Tomar foto
                <IconGeneric className="text-white ms-1" as={MdCameraAlt} />
              </ButtonStyled>
            </Box>
            <Box
              {...getRootProps()}
              borderWidth={2}
              borderStyle="dashed"
              borderColor={isDragActive ? "blue.400" : "gray.300"}
              borderRadius="md"
              p={6}
              textAlign="center"
              bg={isDragActive ? "blue.50" : "gray.50"}
              cursor="pointer"
              transition="all 0.2s"
              _hover={{
                bg: "gray.100",
                borderColor: "blue.400",
              }}
              mb={3}
            >
              <input {...getInputProps()} />
              <Box display="flex" flexDirection="column" alignItems="center">
                <IconGeneric
                  as={BiImageAdd}
                  fontSize="3xl"
                  color={isDragActive ? "blue.500" : "gray.400"}
                  mb={2}
                />
                <Text fontSize="md" fontWeight="medium" color="gray.700">
                  {isDragActive
                    ? "Suelta las imágenes aquí"
                    : "Arrastra y suelta imágenes aquí, o haz clic para seleccionar"}
                </Text>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Formatos: PNG, JPG, JPEG, GIF, BMP, WEBP (máx. 3MB por imagen)
                </Text>
              </Box>
            </Box>
          </>
        ) : null}
        <input
          onChange={handleImageSelection}
          accept="image/*"
          type="file"
          id={displayInputId}
          multiple
          style={{ display: "none" }}
          aria-label="Subir imágenes"
        />
        <input
          onChange={handleCameraCapture}
          accept="image/*"
          type="file"
          id={cameraInputId}
          capture="environment"
          style={{ display: "none" }}
          aria-label="Tomar foto con cámara"
        />

        {uploadProgress > 0 && uploadProgress < 100 && (
          <Box mt={3}>
            <Text>Subiendo imágenes...</Text>
            <Progress value={uploadProgress} size="sm" colorScheme="blue" />
          </Box>
        )}

        <Divider my={3} />
        <Grid templateColumns="repeat(auto-fill, minmax(198px, 1fr))" gap={1}>
          {isArrayNotEmpty(photography) &&
            photography.map((item, index) => (
              <GridItem key={index}>
                <ReactCardWithIcon
                  w="200px"
                  photografyUrl={item.photografy_url}
                  photografyAlt={item.name_photography}
                  Icon={MdClear}
                  ColorIcon="rgb(175, 10, 10)"
                  ActionIcon={() => deleteImaServer(item)}
                  onClickImageButton={() => openImageView(item.photografy_url)}
                  activeDelete={canManagePhotos}
                />
              </GridItem>
            ))}
        </Grid>
      </Box>
    </>
  );
};
